/**
 * AccountDetailsSection - TASK-371 (extracted from ClientProfileForm)
 * Section 1: Account holder personal details, contact info, account type.
 */

import React from 'react';
import { InheritedBadge } from './InheritedBadge';
import type { ClientProfileFormData } from './ClientProfileForm';

type AccountDetails = ClientProfileFormData['account_details'];

interface AccountDetailsSectionProps {
  data: AccountDetails;
  onChange: (field: string, value: any) => void;
  errors: Record<string, string>;
  isInherited: boolean;
}

import { fieldError } from '../../../utils/formHelpers';

export const AccountDetailsSection: React.FC<AccountDetailsSectionProps> = ({
  data,
  onChange,
  errors,
  isInherited,
}) => (
  <div className="px-4 pb-4 space-y-4">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Account Holder Name *
          {isInherited && <InheritedBadge />}
        </label>
        <input
          type="text"
          value={data.holder_name || ''}
          onChange={(e) => onChange('holder_name', e.target.value)}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors['account_details.holder_name'] ? 'border-red-300' : 'border-gray-200'}`}
          placeholder="Full legal name"
        />
        {fieldError(errors, 'account_details.holder_name')}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Date of Birth *
          {isInherited && <InheritedBadge />}
        </label>
        <input
          type="date"
          value={data.date_of_birth || ''}
          onChange={(e) => onChange('date_of_birth', e.target.value)}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors['account_details.date_of_birth'] ? 'border-red-300' : 'border-gray-200'}`}
        />
        {fieldError(errors, 'account_details.date_of_birth')}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Nationality *
          {isInherited && <InheritedBadge />}
        </label>
        <input
          type="text"
          value={data.nationality || ''}
          onChange={(e) => onChange('nationality', e.target.value)}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors['account_details.nationality'] ? 'border-red-300' : 'border-gray-200'}`}
          placeholder="e.g. Swiss"
        />
        {fieldError(errors, 'account_details.nationality')}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Phone *
          {isInherited && <InheritedBadge />}
        </label>
        <input
          type="tel"
          value={data.phone || ''}
          onChange={(e) => onChange('phone', e.target.value)}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors['account_details.phone'] ? 'border-red-300' : 'border-gray-200'}`}
          placeholder="+41 XX XXX XX XX"
        />
        {fieldError(errors, 'account_details.phone')}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Email *
          {isInherited && <InheritedBadge />}
        </label>
        <input
          type="email"
          value={data.email || ''}
          onChange={(e) => onChange('email', e.target.value)}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors['account_details.email'] ? 'border-red-300' : 'border-gray-200'}`}
          placeholder="email@example.com"
        />
        {fieldError(errors, 'account_details.email')}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Tax Residence
          {isInherited && <InheritedBadge />}
        </label>
        <input
          type="text"
          value={data.tax_residence || ''}
          onChange={(e) => onChange('tax_residence', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Country of tax residence"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Account Type
        </label>
        <select
          value={data.account_type || ''}
          onChange={(e) => onChange('account_type', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select account type</option>
          <option value="individual">Individual</option>
          <option value="joint">Joint</option>
          <option value="corporate">Corporate</option>
          <option value="trust">Trust</option>
          <option value="foundation">Foundation</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Currency
        </label>
        <select
          value={data.currency || ''}
          onChange={(e) => onChange('currency', e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select currency</option>
          <option value="CHF">CHF</option>
          <option value="EUR">EUR</option>
          <option value="USD">USD</option>
          <option value="GBP">GBP</option>
        </select>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        id="us_person"
        checked={data.us_person || false}
        onChange={(e) => onChange('us_person', e.target.checked)}
        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
      />
      <label htmlFor="us_person" className="text-sm text-gray-700">
        US Person (FATCA)
        {isInherited && <InheritedBadge />}
      </label>
    </div>
  </div>
);

export default AccountDetailsSection;
